"use client";
import { PopoverScopedContextProvider, usePopoverProviderContext } from "./popover-context.js";
import { createDialogComponent, useDialog } from "../dialog/dialog.js";
import { t as getBasePlacement } from "../__chunks/B3k5_R1L.js";
import { createElement, createHook, forwardRef, useEvent, usePortalRef, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { invariant } from "@ariakit/utils";
import { useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
import { arrow, autoUpdate, computePosition, flip, limitShift, offset, shift, size } from "@floating-ui/dom";
//#region src/popover/popover.tsx
const TagName = "div";
function createDOMRect(x = 0, y = 0, width = 0, height = 0) {
	if (typeof DOMRect === "function") return new DOMRect(x, y, width, height);
	const rect = {
		x,
		y,
		width,
		height,
		top: y,
		right: x + width,
		bottom: y + height,
		left: x
	};
	return {
		...rect,
		toJSON: () => rect
	};
}
function getDOMRect(anchorRect) {
	if (!anchorRect) return createDOMRect();
	const { x, y, width, height } = anchorRect;
	return createDOMRect(x, y, width, height);
}
function getAnchorElement(anchorElement, getAnchorRect) {
	return {
		contextElement: anchorElement || void 0,
		getBoundingClientRect: () => {
			const anchor = anchorElement;
			const anchorRect = getAnchorRect?.(anchor);
			if (anchorRect || !anchor) return getDOMRect(anchorRect);
			return anchor.getBoundingClientRect();
		}
	};
}
function isValidPlacement(flip) {
	return /^(?:top|bottom|left|right)(?:-(?:start|end))?$/.test(flip);
}
function roundByDPR(value) {
	const dpr = window.devicePixelRatio || 1;
	return Math.round(value * dpr) / dpr;
}
function getOffsetMiddleware(arrowElement, props) {
	return offset(({ placement }) => {
		const arrowOffset = (arrowElement?.clientHeight || 0) / 2;
		const finalGutter = typeof props.gutter === "number" ? props.gutter + arrowOffset : props.gutter ?? arrowOffset;
		return {
			crossAxis: !!!placement.split("-")[1] ? props.shift : void 0,
			mainAxis: finalGutter,
			alignmentAxis: props.shift
		};
	});
}
function getFlipMiddleware(props) {
	if (props.flip === false) return;
	const fallbackPlacements = typeof props.flip === "string" ? props.flip.split(" ") : void 0;
	invariant(!fallbackPlacements || fallbackPlacements.every(isValidPlacement), process.env.NODE_ENV !== "production" && "`flip` expects a spaced-delimited list of placements");
	return flip({
		padding: props.overflowPadding,
		fallbackPlacements
	});
}
function getShiftMiddleware(props) {
	if (!props.slide && !props.overlap) return;
	return shift({
		mainAxis: props.slide,
		crossAxis: props.overlap,
		padding: props.overflowPadding,
		limiter: limitShift()
	});
}
function getSizeMiddleware(props, shouldCancel) {
	return size({
		padding: props.overflowPadding,
		apply({ elements, availableWidth, availableHeight, rects }) {
			if (shouldCancel?.()) return;
			const wrapper = elements.floating;
			const referenceWidth = Math.round(rects.reference.width);
			availableWidth = Math.floor(availableWidth);
			availableHeight = Math.floor(availableHeight);
			wrapper.style.setProperty("--popover-anchor-width", `${referenceWidth}px`);
			wrapper.style.setProperty("--popover-available-width", `${availableWidth}px`);
			wrapper.style.setProperty("--popover-available-height", `${availableHeight}px`);
			if (props.sameWidth) wrapper.style.width = `${referenceWidth}px`;
			if (props.fitViewport) {
				wrapper.style.maxWidth = `${availableWidth}px`;
				wrapper.style.maxHeight = `${availableHeight}px`;
			}
		}
	});
}
function getArrowMiddleware(arrowElement, props) {
	if (!arrowElement) return;
	return arrow({
		element: arrowElement,
		padding: props.arrowPadding
	});
}
/**
* Returns props to create a `Popover` component.
* @see https://ariakit.com/components/popover
* @example
* ```jsx
* const store = usePopoverStore();
* const props = usePopover({ store });
* <Role {...props}>Popover</Role>
* ```
*/
const usePopover = createHook(function usePopover({ store, modal = false, portal = modal, preserveTabOrder = true, autoFocusOnShow = true, wrapperProps, fixed = false, flip = true, shift = 0, slide = true, overlap = false, sameWidth = false, fitViewport = false, gutter, arrowPadding = 4, overflowPadding = 8, getAnchorRect, updatePosition, ...props }) {
	const context = usePopoverProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "Popover must receive a `store` prop or be wrapped in a PopoverProvider component.");
	const arrowElement = useStoreState(store, "arrowElement");
	const anchorElement = useStoreState(store, "anchorElement");
	const disclosureElement = useStoreState(store, "disclosureElement");
	const popoverElement = useStoreState(store, "popoverElement");
	const contentElement = useStoreState(store, "contentElement");
	const placement = useStoreState(store, "placement");
	const mounted = useStoreState(store, "mounted");
	const rendered = useStoreState(store, "rendered");
	const defaultArrowElementRef = useRef(null);
	const [positioned, setPositioned] = useState(false);
	const { portalRef, domReady } = usePortalRef(portal, props.portalRef);
	const getAnchorRectProp = useEvent(getAnchorRect);
	const updatePositionProp = useEvent(updatePosition);
	const hasCustomUpdatePosition = !!updatePosition;
	useSafeLayoutEffect(() => {
		if (!popoverElement?.isConnected) return;
		popoverElement.style.setProperty("--popover-overflow-padding", `${overflowPadding}px`);
		const anchor = getAnchorElement(anchorElement, getAnchorRectProp);
		let canceled = false;
		const shouldCancelUpdate = () => {
			if (canceled) return true;
			if (!popoverElement.isConnected) return true;
			return false;
		};
		const updatePosition = async () => {
			if (shouldCancelUpdate()) return;
			if (!mounted) return;
			if (!arrowElement) defaultArrowElementRef.current = defaultArrowElementRef.current || document.createElement("div");
			const arrow = arrowElement || defaultArrowElementRef.current;
			const middleware = [
				getOffsetMiddleware(arrow, {
					gutter,
					shift
				}),
				getFlipMiddleware({
					flip,
					overflowPadding
				}),
				getShiftMiddleware({
					slide,
					shift,
					overlap,
					overflowPadding
				}),
				getArrowMiddleware(arrow, { arrowPadding }),
				getSizeMiddleware({
					sameWidth,
					fitViewport,
					overflowPadding
				}, shouldCancelUpdate)
			];
			const pos = await computePosition(anchor, popoverElement, {
				placement,
				strategy: fixed ? "fixed" : "absolute",
				middleware
			});
			if (shouldCancelUpdate()) return;
			store?.setState("currentPlacement", pos.placement);
			setPositioned(true);
			const x = roundByDPR(pos.x);
			const y = roundByDPR(pos.y);
			Object.assign(popoverElement.style, {
				top: "0",
				left: "0",
				transform: `translate3d(${x}px,${y}px,0)`
			});
			if (arrow && pos.middlewareData.arrow) {
				const { x: arrowX, y: arrowY } = pos.middlewareData.arrow;
				const side = getBasePlacement(pos.placement);
				const centerX = arrow.clientWidth / 2;
				const centerY = arrow.clientHeight / 2;
				const originX = arrowX != null ? arrowX + centerX : -centerX;
				const originY = arrowY != null ? arrowY + centerY : -centerY;
				popoverElement.style.setProperty("--popover-transform-origin", {
					top: `${originX}px calc(100% + ${centerY}px)`,
					bottom: `${originX}px ${-centerY}px`,
					left: `calc(100% + ${centerX}px) ${originY}px`,
					right: `${-centerX}px ${originY}px`
				}[side]);
				Object.assign(arrow.style, {
					left: arrowX != null ? `${arrowX}px` : "",
					top: arrowY != null ? `${arrowY}px` : "",
					right: "",
					bottom: "",
					[side]: "100%"
				});
			}
		};
		const update = async () => {
			if (shouldCancelUpdate()) return;
			if (hasCustomUpdatePosition) {
				await updatePositionProp({ updatePosition });
				if (shouldCancelUpdate()) return;
				setPositioned(true);
			} else await updatePosition();
		};
		const cancelAutoUpdate = autoUpdate(anchor, popoverElement, update, { elementResize: typeof ResizeObserver === "function" });
		return () => {
			canceled = true;
			setPositioned(false);
			cancelAutoUpdate();
		};
	}, [
		store,
		rendered,
		popoverElement,
		arrowElement,
		anchorElement,
		placement,
		mounted,
		domReady,
		fixed,
		flip,
		shift,
		slide,
		overlap,
		sameWidth,
		fitViewport,
		gutter,
		arrowPadding,
		overflowPadding,
		getAnchorRectProp,
		hasCustomUpdatePosition,
		updatePositionProp
	]);
	useSafeLayoutEffect(() => {
		if (!mounted) return;
		if (!domReady) return;
		if (!popoverElement?.isConnected) return;
		if (!contentElement?.isConnected) return;
		const applyZIndex = () => {
			popoverElement.style.zIndex = getComputedStyle(contentElement).zIndex;
		};
		applyZIndex();
		let raf = requestAnimationFrame(() => {
			raf = requestAnimationFrame(applyZIndex);
		});
		return () => cancelAnimationFrame(raf);
	}, [
		mounted,
		domReady,
		popoverElement,
		contentElement
	]);
	const position = fixed ? "fixed" : "absolute";
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx("div", {
		...wrapperProps,
		style: {
			position,
			top: 0,
			left: 0,
			width: "max-content",
			...wrapperProps?.style
		},
		ref: store?.setPopoverElement,
		children: element
	}), [
		store,
		position,
		wrapperProps
	]);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(PopoverScopedContextProvider, {
		value: store,
		children: element
	}), [store]);
	props = {
		"data-placing": !positioned || void 0,
		...props,
		style: {
			position: "relative",
			...props.style
		}
	};
	props = useDialog({
		store,
		modal,
		portal,
		preserveTabOrder,
		preserveTabOrderAnchor: disclosureElement || anchorElement,
		autoFocusOnShow: positioned && autoFocusOnShow,
		...props,
		portalRef
	});
	return props;
});
/**
* Renders a popover element that's automatically positioned relative to an
* anchor element.
* @see https://ariakit.com/components/popover
* @example
* ```jsx {3}
* <PopoverProvider>
*   <PopoverDisclosure>Disclosure</PopoverDisclosure>
*   <Popover>Popover</Popover>
* </PopoverProvider>
* ```
*/
const Popover = createDialogComponent(forwardRef(function Popover(props) {
	return createElement(TagName, usePopover(props));
}), usePopoverProviderContext);
//#endregion
export { Popover, usePopover };

//# sourceMappingURL=popover.js.map